import type { VisibilityState } from "@tanstack/react-table";
import { generatePath } from "react-router-dom";

import { PROJECT_DETAILS_PATH_WITH_TAB_ID } from "~/constants/appRoutes.ts";
import type { BillingSettings } from "~/modules/billing/api/billingSettings/billingSettingsTypes.ts";
import {
	type BillingSettingsDivergingFields,
	type BillingSettingsLookupData,
	computeBillingSettingsDifferences,
	type TableLink,
} from "~/modules/billing/utils/billingSettingsDisplayUtils.ts";
import type { OrderWithBillingSettings } from "~/modules/project/api/order/orderTypes.ts";
import type { ProjectPhase } from "~/modules/project/api/projectPhase/projectPhaseTypes.ts";
import { getFullPhaseTitle } from "~/modules/project/utils/projectPhaseUtils.ts";

export type DivergingOrderBillingSettingsRow = BillingSettingsDivergingFields & {
	orderId: string;
	phaseAndOrderName: TableLink;
};

export function buildDivergingOrderBillingSettingsTableData(
	orders: OrderWithBillingSettings[],
	phases: ProjectPhase[],
	projectBillingSettings: BillingSettings,
	lookups: BillingSettingsLookupData,
	clientId: string,
): DivergingOrderBillingSettingsRow[] {
	const phasesById = new Map(phases.map((p) => [p.id, p]));

	return orders
		.filter(
			(order): order is OrderWithBillingSettings & { billingSettings: BillingSettings } =>
				order.billingSettings !== null,
		)
		.map((order) => {
			const phase = phasesById.get(order.projectPhaseId)!;
			const projectId = phase.projectId;
			const tabId = `phase-${phase.phaseNumber}`;

			return {
				orderId: order.id,
				phaseAndOrderName: {
					displayName: `${getFullPhaseTitle(phase)} - ${order.title}`,
					url: generatePath(PROJECT_DETAILS_PATH_WITH_TAB_ID, { projectId, tabId }),
				},
				...computeBillingSettingsDifferences(order.billingSettings, projectBillingSettings, lookups, clientId),
			};
		});
}

const DIFFABLE_COLUMNS = [
	"invoiceTypeId",
	"isFixedPrice",
	"isSkillBased",
	"travelExpensesIncluded",
	"isBilledMonthly",
	"paymentTargetDays",
	"currencyDisplayName",
	"resolvedInvoiceRecipient",
	"invoiceByMail",
	"additionalEmailRecipients",
	"billingInstructions",
] as const satisfies readonly (keyof DivergingOrderBillingSettingsRow)[];

export function computeColumnVisibility(rows: DivergingOrderBillingSettingsRow[]): VisibilityState {
	const visibility: VisibilityState = {};

	for (const column of DIFFABLE_COLUMNS) {
		visibility[column] = rows.some((row) => row[column].isDifferent);
	}

	return visibility;
}